import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const plans = [
  { 
    id: 'starter',
    name: "Starter", 
    price: 22.99, 
    features: [
      "Up to 15 vehicle listings",
      "Basic VIN decoder",
      "Profit calculator",
      "Email support"
    ],
    popular: false
  },
  { 
    id: 'professional',
    name: "Professional", 
    price: 59.99, 
    features: [
      "Up to 50 vehicle listings",
      "Advanced VIN decoder",
      "Profit calculator",
      "Customer lead tracking",
      "Expense & repair tracking",
      "Basic analytics",
      "Priority email support"
    ],
    popular: true
  },
  { 
    id: 'dealer_pro',
    name: "Dealer Pro", 
    price: 99.99, 
    features: [
      "Unlimited vehicle listings",
      "Wakzy Check vehicle history reports",
      "Full VIN decode with safety & recall data",
      "Advanced customer tracking & CRM",
      "Full analytics dashboard",
      "AI description generator",
      "AI pricing suggestions",
      "Sales invoices & expense tracking",
      "Phone & email support",
      "Document storage"
    ],
    popular: false
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const handleGetStarted = (planId) => {
    if (user) {
      navigate(`/dashboard?subscribe=${planId}&billing=${billingPeriod}`);
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo">
              <img 
                src="/wakzy-logo.png" 
                alt="Wakzy Auto Tech" 
                className="h-12 w-auto"
              />
            </Link>
            
            <div className="flex items-center gap-6">
              <Link to="/" className="text-[#52525B] hover:text-[#0A0A0A] transition-colors font-medium">
                Home
              </Link>
              {user ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Link to="/login" className="text-[#52525B] hover:text-[#0A0A0A] transition-colors font-medium">
                    Sign In
                  </Link>
                  <Button 
                    onClick={() => navigate('/register')}
                    className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="label-caps text-[#0052FF] mb-4 block">PRICING</span>
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#0A0A0A] leading-none tracking-tight mb-6"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            data-testid="pricing-hero-title"
          >
            Choose Your Plan
          </h1>
          <p className="text-lg sm:text-xl text-[#52525B] mb-8 max-w-2xl mx-auto">
            Start with a 7-day free trial. No credit card required. Cancel anytime.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white rounded-full p-1 border border-[rgba(10,10,10,0.1)]">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full transition-colors font-medium ${
                billingPeriod === 'monthly' ? 'bg-[#0052FF] text-white' : 'text-[#52525B] hover:text-[#0A0A0A]'
              }`}
              data-testid="billing-monthly-btn"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-full transition-colors font-medium ${
                billingPeriod === 'yearly' ? 'bg-[#0052FF] text-white' : 'text-[#52525B] hover:text-[#0A0A0A]'
              }`}
              data-testid="billing-yearly-btn"
            >
              Yearly <span className="text-xs ml-1 opacity-70">Save 20%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, idx) => (
              <div 
                key={idx}
                className={`relative bg-white rounded-md p-8 ${plan.popular ? 'ring-2 ring-[#0052FF] shadow-xl scale-105' : 'border border-[rgba(10,10,10,0.1)]'}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#0052FF] text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-wide">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 
                    className="text-2xl font-bold text-[#0A0A0A] mb-4" 
                    style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
                  >
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-black text-[#0A0A0A]">
                      ${billingPeriod === 'yearly' ? (plan.price * 0.8).toFixed(0) : plan.price}
                    </span>
                    <span className="text-[#52525B]">/month</span>
                  </div>
                  {billingPeriod === 'yearly' && (
                    <div className="mt-2">
                      <p className="text-sm text-[#16A34A] font-semibold">
                        Billed ${(plan.price * 0.8 * 12).toFixed(0)}/year
                      </p>
                      <p className="text-xs text-[#16A34A]">
                        You save ${(plan.price * 12 * 0.2).toFixed(0)}/year
                      </p>
                    </div>
                  )}
                  {billingPeriod === 'monthly' && (
                    <p className="text-xs text-gray-400 mt-2">
                      Save 20% with yearly billing
                    </p>
                  )}
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                      <span className="text-[#52525B]">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => handleGetStarted(plan.id)}
                  className={`w-full h-12 font-semibold ${
                    plan.popular 
                      ? 'bg-[#0052FF] hover:bg-[#0043D1] text-white' 
                      : 'bg-white border-2 border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF] hover:text-white'
                  }`}
                  data-testid={`plan-cta-${plan.id}`}
                >
                  Get Started <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-[#F4F4F5]">
        <div className="max-w-3xl mx-auto px-6">
          <h2 
            className="text-3xl font-bold text-center text-[#0A0A0A] mb-12 tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {[
              { q: "Can I switch plans later?", a: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately." },
              { q: "Is there a free trial?", a: "Yes! All plans come with a 7-day free trial. No credit card required to start." },
              { q: "What payment methods do you accept?", a: "We accept all major credit cards through Stripe, our secure payment processor." },
              { q: "Can I cancel anytime?", a: "Absolutely. You can cancel your subscription at any time from your dashboard." },
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-md p-6 border border-[rgba(10,10,10,0.1)]">
                <h3 className="font-bold text-[#0A0A0A] mb-2">{item.q}</h3>
                <p className="text-[#52525B]">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[rgba(10,10,10,0.1)]">
        <div className="max-w-7xl mx-auto px-6 text-center text-[#52525B]">
          <p>&copy; {new Date().getFullYear()} Wakzy Auto Tech. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
